import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
	Plus,
	Star,
	MessageSquare,
	Eye,
	Edit,
	Code,
	Loader2,
	Play,
	Pause,
	Trash2,
	Search,
	Layout,
	ChevronDown,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

import { useProgram } from '@/hooks/useProgram'
import { widgetApi } from '@/services/api'
import { WidgetsPageSkeleton } from '@/components/widgets/WidgetsPageSkeleton'
import { LanguageConfigurationTip } from '@/components/ui/language-tip'
import { useTranslation } from '@/hooks/useTranslation'
import { usePlanInfo } from '@/hooks/usePlanInfo'
import { WidgetInstallation } from '@/components/widget/WidgetInstallation'
import { EmbedWidgetContainer } from '@/components/widget/EmbedWidget'

const FreeTierNotice = ({ hasActiveWidget }: { hasActiveWidget: boolean }) => {
	const { t } = useTranslation()
	const navigate = useNavigate()

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5, delay: 0.3 }}
			className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
			<div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
				<div>
					<h3 className="text-lg font-semibold text-blue-700 dark:text-blue-300 mb-1">
						{t('widgets.freeTierLimitation')}
					</h3>
					<p className="text-sm text-blue-600/80 dark:text-blue-400/80">
						{t('widgets.freePlanAllowsMultiple')}
						{hasActiveWidget && ` ${t('widgets.alreadyHaveActiveWidget')}`}
					</p>
				</div>
				<Button
					onClick={() => navigate('/settings?tab=billing')}
					className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-none shadow-lg hover:shadow-xl transition-all duration-300">
					{t('widgets.upgradeToPro')}
				</Button>
			</div>
		</motion.div>
	)
}

const Widgets = () => {
	const { t } = useTranslation()
	const { programId } = useProgram()

	const navigate = useNavigate()
	const { toast } = useToast()
	const [searchQuery, setSearchQuery] = React.useState('')
	const [filterType, setFilterType] = React.useState('all')
	const [loadingWidgetIds, setLoadingWidgetIds] = useState<string[]>([])
	const [installCodeWidgetId, setInstallCodeWidgetId] = useState<string | null>(null)
	const [testWidgetId, setTestWidgetId] = useState<string | null>(null)
	const [hasActiveWidget, setHasActiveWidget] = useState(false)
	const { data: planInfo } = usePlanInfo()

	const {
		data: widgetsData = { data: [], totalCount: 0 },
		isLoading,
		error,
		refetch,
	} = useQuery({
		queryKey: ['widgets', programId],
		queryFn: () => {
			if (!programId) {
				return Promise.resolve({ data: [], totalCount: 0 })
			}
			return widgetApi.getWidgets(programId)
		},
		enabled: !!programId,
	})

	const widgets = widgetsData.data

	// Check if there's an active widget when widgets data changes
	useEffect(() => {
		if (Array.isArray(widgets) && widgets.length > 0) {
			setHasActiveWidget(widgets.some((widget) => widget.is_active))
		} else {
			setHasActiveWidget(false)
		}
	}, [widgets])

	// Handle query error effectfully
	useEffect(() => {
		if (error) {
			console.error('Error fetching widgets:', error)
			toast({
				variant: 'destructive',
				title: t('widgets.error'),
				description:
					error instanceof Error ? error.message : t('widgets.failedToLoadWidgets'),
			})
		}
	}, [error, t, toast])

	const deleteWidget = useMutation({
		mutationFn: widgetApi.deleteWidget,
		onSuccess: () => {
			refetch()
			toast({
				title: t('widgets.success'),
				description: t('widgets.widgetDeletedSuccessfully'),
			})
		},
		onError: (error) => {
			console.error('Error deleting widget:', error)
			toast({
				title: t('widgets.error'),
				description: t('widgets.failedToDeleteWidget'),
				variant: 'destructive',
			})
		},
	})

	const toggleWidgetActive = useMutation({
		mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
			setLoadingWidgetIds((prev) => [...prev, id])
			try {
				const result = await widgetApi.updateWidget(id, { is_active: isActive })
				return result
			} finally {
				setLoadingWidgetIds((prev) =>
					prev.filter((widgetId) => widgetId !== id)
				)
			}
		},
		onSuccess: (result, variables) => {
			if (result?.error?.code === 'activation_limit_reached') {
				toast({
					title: t('widgets.freeTierLimitation'),
					description: (
						<div className="flex flex-col gap-3">
							<p>{result.error.message}</p>
							<Button
								className="mt-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium"
								onClick={() => navigate('/settings?tab=billing')}>
								{t('widgets.upgradeToPro')}
							</Button>
						</div>
					),
					variant: 'default',
					duration: 10000,
				})
			} else {
				refetch()
				toast({
					title: t('widgets.success'),
					description: variables.isActive 
						? t('widgets.widgetActivatedSuccessfully')
						: t('widgets.widgetPausedSuccessfully'),
				})
			}
		},
		onError: (error) => {
			console.error('Error updating widget status:', error)
			toast({
				title: t('widgets.error'),
				description:
					error instanceof Error
						? error.message
						: t('widgets.failedToUpdateWidgetStatus'),
				variant: 'destructive',
			})
		},
	})

	const filteredWidgets = React.useMemo(() => {
		if (!Array.isArray(widgets)) return [] // Ensure widgets is an array
		return widgets.filter((widget) => {
			const widgetName = widget?.name || ''
			const widgetDescription = widget?.description || ''
			const searchQueryLower = searchQuery.toLowerCase()

			const matchesSearch =
				widgetName.toLowerCase().includes(searchQueryLower) ||
				widgetDescription.toLowerCase().includes(searchQueryLower)
			const matchesType = filterType === 'all' || widget?.type === filterType
			return matchesSearch && matchesType
		})
	}, [widgets, searchQuery, filterType])

	// *** RENDER SKELETON ***
	if (isLoading) {
		return <WidgetsPageSkeleton />
	}

	return (
		<div className="container py-6 space-y-6">
			{/* Header */}
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
				className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-sm border border-border/50">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<Layout className="h-5 w-5 text-primary" />
					</div>
					<div className="flex-1">
						<h1 className="text-xl md:text-2xl font-semibold text-foreground">
							{t('widgets.yourWidgets')}
						</h1>
						<p className="text-sm text-muted-foreground mt-0.5">
							{t('widgets.createAndManageWidgets')}
						</p>
					</div>
					<Button
						onClick={() => navigate('/widgets/new')}
						className="bg-primary hover:bg-primary/90">
						<Plus className="h-4 w-4 mr-2" />
						{t('widgets.createWidget')}
					</Button>
				</div>
			</motion.div>

			{planInfo?.planId === 'free' && (
				<FreeTierNotice hasActiveWidget={hasActiveWidget} />
			)}

			{/* Language Configuration Tip */}
			<motion.div
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.1 }}>
				<LanguageConfigurationTip showInstallationHint={false} />
			</motion.div>

			{/* Filter Section */}
			<motion.div
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.2 }}
				className="flex flex-col sm:flex-row gap-4 mb-8">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t('widgets.searchWidgets')}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-10 bg-white dark:bg-slate-900 shadow-sm hover:shadow transition-shadow"
					/>
				</div>
			</motion.div>

			{/* Your Widgets Section */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.6 }}
				className="space-y-4">
				<h2 className="text-lg font-semibold">{t('widgets.yourWidgets')}</h2>
				{filteredWidgets.length === 0 ? (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						className="text-center py-12">
						<div className="mx-auto w-16 h-16 mb-4 bg-primary/10 rounded-full flex items-center justify-center">
							<Plus className="h-8 w-8 text-primary" />
						</div>
						<h3 className="text-lg font-medium mb-2">{t('widgets.noWidgetsFound')}</h3>
						<p className="text-sm text-muted-foreground mb-4">
							{searchQuery || filterType !== 'all'
								? t('widgets.tryAdjustingSearch')
								: t('widgets.createFirstWidgetOrUseTemplate')}
						</p>
						<Button
							onClick={() => navigate('/widgets/new')}
							variant="outline"
							className="bg-white dark:bg-slate-900">
							{t('widgets.createWidget')}
						</Button>
					</motion.div>
				) : (
					<div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
						{filteredWidgets.map((widget) => (
							<motion.div
								key={widget.id}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.3 }}
								className="group">
								<Card
									onClick={(event) => {
										const target = event.target as HTMLElement
										if (target.closest('button, a')) return
										navigate(`/widgets/edit/${widget.id}`)
									}}
									className="overflow-hidden cursor-pointer bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 border-2 border-transparent hover:border-primary/20 transition-all duration-300">
									<CardHeader className="space-y-2">
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-3">
												
												<CardTitle className="text-lg">{widget.name}</CardTitle>
											</div>
											<div className="flex items-center gap-2">
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<Button
															variant="ghost"
															className={
																widget.is_active
																	? 'h-7 rounded-full px-2.5 text-xs font-medium bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-900'
																	: 'h-7 rounded-full px-2.5 text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-800'
															}
															disabled={loadingWidgetIds.includes(widget.id)}>
															<span className="leading-none">
																{widget.is_active
																	? t('widgets.active')
																	: t('widgets.inactive')}
															</span>
															{loadingWidgetIds.includes(widget.id) ? (
																<Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin" />
															) : (
																<ChevronDown className="ml-1.5 h-3.5 w-3.5" />
															)}
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															onClick={() =>
																toggleWidgetActive.mutate({
																	id: widget.id,
																	isActive: !widget.is_active,
																})
															}
															disabled={loadingWidgetIds.includes(widget.id)}>
															{widget.is_active ? (
																<Pause className="mr-2 h-4 w-4" />
															) : (
																<Play className="mr-2 h-4 w-4" />
															)}
															{widget.is_active
																? t('widgetDetails.pauseWidget')
																: t('widgetDetails.activateWidget')}
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
												<AlertDialog>
													<AlertDialogTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-7 w-7 rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/40">
															<Trash2 className="h-4 w-4" />
														</Button>
													</AlertDialogTrigger>
													<AlertDialogContent>
														<AlertDialogHeader>
															<AlertDialogTitle>{t('widgets.deleteWidget')}</AlertDialogTitle>
															<AlertDialogDescription>
																{t('widgets.deleteWidgetConfirmation')}
															</AlertDialogDescription>
														</AlertDialogHeader>
														<AlertDialogFooter>
															<AlertDialogCancel>{t('widgets.cancel')}</AlertDialogCancel>
															<AlertDialogAction
																onClick={() => deleteWidget.mutate(widget.id)}
																className="bg-red-600 hover:bg-red-700 text-white">
																{deleteWidget.isPending ? (
																	<Loader2 className="h-4 w-4 animate-spin" />
																) : (
																	t('widgets.delete')
																)}
															</AlertDialogAction>
														</AlertDialogFooter>
													</AlertDialogContent>
												</AlertDialog>
											</div>
										</div>
										<p className="text-sm text-muted-foreground line-clamp-2">
											{widget.description}
										</p>
									</CardHeader>
									<CardContent>
										<div className="p-4 bg-white/60 dark:bg-slate-800/60 rounded-lg mb-4">
											<div className="flex items-center justify-center min-h-20">
												<div
													className="inline-flex items-center gap-2 rounded-full px-4 py-2 shadow-sm border border-black/5"
													style={{
														backgroundColor:
															widget.feedback_button_color ||
															widget.primary_color ||
															'#000000',
														color: widget.feedback_button_text_color || '#FFFFFF',
													}}>
													<MessageSquare className="h-4 w-4" />
													<span className="text-sm font-medium leading-none">
														{widget.feedback_button_text || 'Feedback'}
													</span>
												</div>
											</div>
										</div>
										<div className="flex flex-wrap gap-2 mt-2">
											{widget.modules?.survey && (
												<span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded-full">
													{t('widgets.surveys')}
												</span>
											)}
											{widget.modules?.feature_requests && (
												<span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded-full">
													{t('widgets.featureRequests')}
												</span>
											)}
											{widget.modules?.bug_reports && (
												<span className="text-xs px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded-full">
													{t('widgets.bugReports')}
												</span>
											)}
											{widget.modules?.reviews && (
												<span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 rounded-full">
													{t('widgets.reviews')}
												</span>
											)}
										</div>
										<div className="flex flex-wrap gap-2">
											<Button
												variant="outline"
												size="sm"
												onClick={() => navigate(`/widgets/edit/${widget.id}`)}
												className="bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-800 group-hover:border-primary/50"
												title={t('widgetDetails.edit')}>
												<Edit className="h-4 w-4 group-hover:scale-110 transition-transform" />
											</Button>
											<Button
												variant="outline"
												size="sm"
												onClick={() => setInstallCodeWidgetId(widget.id)}
												className="bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-800 group-hover:border-primary/50">
												<Code className="h-4 w-4 mr-1.5 group-hover:scale-110 transition-transform" />
												{t('widgetDetails.code')}
											</Button>
											<Button
												variant="outline"
												size="sm"
												onClick={() =>
													setTestWidgetId((prev) =>
														prev === widget.id ? null : widget.id
													)
												}
												className="bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-800 group-hover:border-primary/50">
												<Eye className="h-4 w-4 mr-1.5 group-hover:scale-110 transition-transform" />
												{t('widgetDetails.test')}
											</Button>
										</div>
									</CardContent>
								</Card>
							</motion.div>
						))}
					</div>
				)}
			</motion.div>

			<Dialog
				open={!!installCodeWidgetId}
				onOpenChange={(open) => {
					if (!open) setInstallCodeWidgetId(null)
				}}>
				<DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>{t('widgetDetails.code')}</DialogTitle>
					</DialogHeader>
					{installCodeWidgetId && (
						<WidgetInstallation widgetId={installCodeWidgetId} />
					)}
				</DialogContent>
			</Dialog>

			{testWidgetId && (
				<div className="fixed bottom-4 right-4 z-[1000] transition-all duration-300 ease-out">
					<EmbedWidgetContainer
						widgetId={testWidgetId}
						onRemove={() => setTestWidgetId(null)}
						showRemoveButton={true}
					/>
				</div>
			)}
		</div>
	)
}

export default Widgets
